// ✅ src/context/AuthContext.js
// Enhanced version — auto-detects role_code & scope from JWT (data.scopes[0])
// Includes one-time developer console log for role, scope & permissions.

import { createContext, useState, useEffect, useCallback } from "react";
import axiosClient from "../api/axiosClient";
import { jwtDecode } from "jwt-decode";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    user: null,
    permissions: {},
    activeScope: null,
  });

  // ==========================================================
  // 🔹 Load and decode token
  // ==========================================================
  const loadFromToken = useCallback((token) => {
    try {
      const decoded = jwtDecode(token);
      const data = JSON.parse(decoded.sub);

      // ✅ Extract primary scope if available
      const primaryScope = Array.isArray(data.scopes) ? data.scopes[0] : {};

      // ✅ Normalize user fields
      const user = {
        id: data.id,
        username: data.username,
        email: data.email,
        role_code: data.role_code || primaryScope.role_code,
        role_name: data.role_name || primaryScope.role_name,
        role_id: data.role_id || primaryScope.role_id,
        tenant_id: data.tenant_id || primaryScope.tenant_id,
      };

      // ✅ Normalize activeScope
      const activeScope = {
        tenant_id: data.tenant_id || primaryScope.tenant_id || null,
        location_id: data.location_id || primaryScope.location_id || null,
        department_id:
          data.department_id || primaryScope.department_id || null,
        role_id: data.role_id || primaryScope.role_id || null,
        role_code: user.role_code,
      };

      // ✅ Set context state
      setAuth({
        user,
        permissions: data.permissions || {},
        activeScope,
      });

      // ✅ Apply JWT for axios globally
      axiosClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // ==========================================================
      // 🧠 Developer Log (Runs Once on Token Load)
      // ==========================================================
      if (process.env.NODE_ENV === "development") {
        console.groupCollapsed("🔐 AuthContext – Loaded from JWT");
        console.log("👤 User:", user);
        console.log("🏢 Active Scope:", activeScope);
        console.log("🧩 Permission Keys:", Object.keys(data.permissions || {}));
        console.log(
          "SCM_DASHBOARD VALUE:",
          data.permissions?.SCM_DASHBOARD
        );

        console.log(
          "SERVICE_TICKET VALUE:",
          data.permissions?.SERVICE_TICKET
        );
        console.groupEnd();
      }
    } catch (err) {
      console.error("❌ Auth decode error:", err);
      logout();
    }
  }, []);

  // ==========================================================
  // 🔹 Auto-load from token on mount
  // ==========================================================
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token && token !== "undefined") {
      loadFromToken(token);
    }
  }, [loadFromToken]);

  // ==========================================================
  // 🔹 Login handler
  // ==========================================================
  const login = (_, accessToken, refreshToken = null) => {
    localStorage.setItem("access_token", accessToken);

    if (refreshToken) {
        localStorage.setItem("refresh_token", refreshToken);
    }

    loadFromToken(accessToken);
};
  // ==========================================================
  // 🔹 Logout handler
  // ==========================================================
  const logout = () => {
    localStorage.clear();
    delete axiosClient.defaults.headers.common["Authorization"];
    setAuth({ user: null, permissions: {}, activeScope: null });
  };

  // ==========================================================
  // 🔹 Permission Check Helper
  // ==========================================================
  const hasPermission = (feature, action) => {
    return auth.permissions?.[feature]?.[action] || false;
  };

  // ==========================================================
  // ✅ Return Auth Context Provider
  // ==========================================================
  return (
    <AuthContext.Provider value={{ ...auth, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};
